import { calculateHeaderGroupHeight } from '@theme/critical';
import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * RequestAnimationFrame id for resize updates
   * @type {number | null}
   */
  #resizeRafId = null;

  /**
   * RequestAnimationFrame id for scroll-up layout reads
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Last measured header height to prevent redundant writes
   * @type {number | null}
   */
  #lastHeaderHeight = null;

  /**
   * Last measured announcement bar height to prevent redundant writes
   * @type {number | null}
   */
  #lastAnnouncementHeight = null;

  /**
   * Last measured mobile drawer top offset to prevent redundant writes
   * @type {number | null}
   */
  #lastMobileDrawerTop = null;

  /**
   * @param {ResizeObserverEntry} entry
   * @returns {number}
   */
  #getResizeEntryHeight(entry) {
    const borderBoxSize = entry.borderBoxSize;
    if (borderBoxSize?.length) {
      return borderBoxSize[0].blockSize;
    }
    return entry.contentRect.height;
  }

  /**
   * @returns {number | null}
   */
  #readMobileDrawerTop() {
    if (window.innerWidth >= 750) return null;

    const searchRow = document.querySelector('.header__row--search-mobile');
    if (searchRow) {
      return searchRow.getBoundingClientRect().bottom;
    }

    return this.getBoundingClientRect().height;
  }

  /**
   * @param {number} totalHeight
   */
  #applyMobileDrawerTop(totalHeight) {
    if (this.#lastMobileDrawerTop === totalHeight) return;

    this.#lastMobileDrawerTop = totalHeight;

    const menuDrawer = document.querySelector('.menu-drawer');
    const menuDrawerBackdrop = document.querySelector('.menu-drawer__backdrop');

    if (menuDrawer) {
      menuDrawer.style.setProperty('--mobile-drawer-top', `${totalHeight}px`);
    }
    if (menuDrawerBackdrop) {
      menuDrawerBackdrop.style.setProperty('--mobile-drawer-top', `${totalHeight}px`);
    }

    document.body.style.setProperty('--mobile-drawer-top', `${totalHeight}px`);
  }

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   * Also updates mobile drawer position when header-group elements resize
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    if (this.#resizeRafId) return;

    this.#resizeRafId = requestAnimationFrame(() => {
      this.#resizeRafId = null;

      // Batch layout reads before any style writes to avoid forced reflow
      const headerHeight =
        entry.target === this ? this.#getResizeEntryHeight(entry) : this.getBoundingClientRect().height;

      const announcementBar = document.querySelector('.announcement-bar');
      const announcementHeight = announcementBar?.offsetHeight ?? null;
      const mobileDrawerTop = this.#readMobileDrawerTop();

      if (this.#lastHeaderHeight !== headerHeight) {
        document.body.style.setProperty('--header-height', `${headerHeight}px`);
        this.#lastHeaderHeight = headerHeight;
      }

      if (announcementHeight !== null && this.#lastAnnouncementHeight !== announcementHeight) {
        document.body.style.setProperty('--announcement-bar-height', `${announcementHeight}px`);
        this.#lastAnnouncementHeight = announcementHeight;
      }

      if (mobileDrawerTop !== null) {
        this.#applyMobileDrawerTop(mobileDrawerTop);
      }

      // Check if the menu drawer should be hidden in favor of the header menu
      if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
        this.#updateMenuVisibility(false);
      }
    });
  });

  /**
   * Get the effective sticky mode based on current viewport
   * @returns {string | null} The sticky mode to use
   */
  #getEffectiveStickyMode = () => {
    const isMobile = window.innerWidth < 768; // Tailwind's md breakpoint
    const stickyMode = this.getAttribute('sticky');
    const stickyMobileMode = this.getAttribute('sticky-mobile');

    if (isMobile) {
      // On mobile, if sticky-mobile is explicitly set to "never", return null
      if (stickyMobileMode === 'never') return null;
      // If sticky-mobile has a value (always/scroll-up), use it; otherwise use desktop sticky
      return stickyMobileMode || stickyMode;
    } else {
      // On desktop, always use desktop sticky mode
      return stickyMode;
    }
  };

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  #handleWindowScroll = () => {
    const effectiveStickyMode = this.#getEffectiveStickyMode();

    // Exit early if no sticky mode is active
    if (!effectiveStickyMode) return;

    // While the accordion is auto-scrolling, suppress show/hide logic to prevent
    // the header from flashing. Keep lastScrollTop current so direction detection
    // is accurate the moment the lock is released.
    if (this.hasAttribute('data-accordion-scrolling')) {
      this.#lastScrollTop = document.scrollingElement?.scrollTop ?? 0;
      return;
    }

    if (!this.#offscreen && effectiveStickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (effectiveStickyMode === 'always') {
      const isAtTop = this.dataset.stickyState === 'inactive';

      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      if (this.#scrollRafId) cancelAnimationFrame(this.#scrollRafId);

      const capturedScrollTop = scrollTop;
      this.#scrollRafId = requestAnimationFrame(() => {
        this.#scrollRafId = null;

        if (this.getBoundingClientRect().top >= 0) {
          // reset sticky state when header is scrolled up to natural position
          this.#offscreen = false;
          this.dataset.stickyState = 'inactive';
          this.dataset.scrollDirection = 'none';
        } else {
          // show sticky header when scrolling up
          this.dataset.stickyState = 'active';
          this.dataset.scrollDirection = 'up';
        }

        this.#lastScrollTop = capturedScrollTop;
      });
      return;
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      // delay transitioning to idle hidden state for hiding animation
      this.setAttribute('data-animating', '');

      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = 'idle';
        this.removeAttribute('data-animating');
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  /**
   * Handle window resize to re-evaluate sticky behavior
   */
  #handleResize = () => {
    // Disconnect existing observer if it exists
    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }

    // Remove existing scroll listener
    document.removeEventListener('scroll', this.#handleWindowScroll);

    // Update mobile drawer position
    this.#updateMobileDrawerPosition();

    // Re-initialize with new effective sticky mode
    this.#initializeStickyBehavior();
  };

  /**
   * Handle drawer toggle event to recalculate position before opening
   * This fixes the issue where sticky header scroll position causes drawer misalignment
   * @param {Event} event
   */
  #handleDrawerToggle = (event) => {
    const details = event.target;
    // Only recalculate when drawer is opening (details.open is true after toggle)
    if (details instanceof HTMLDetailsElement && details.open) {
      this.#updateMobileDrawerPosition();
    }
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    this.#initializeStickyBehavior();

    // Set initial mobile drawer position
    this.#updateMobileDrawerPosition();

    // Update again after a short delay to catch any late-rendered elements
    setTimeout(() => {
      this.#updateMobileDrawerPosition();
    }, 100);

    // Observe all header-group children for size changes
    const headerGroup = document.querySelector('#header-group');
    if (headerGroup) {
      const children = headerGroup.children;
      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        if (child instanceof HTMLElement) {
          this.#resizeObserver.observe(child);
        }
      }
    }

    // Listen for window resize to re-evaluate sticky mode
    window.addEventListener('resize', this.#handleResize);

    // Listen for drawer toggle to recalculate position before opening
    const drawerContainer = document.querySelector('#Details-menu-drawer-container');
    if (drawerContainer) {
      drawerContainer.addEventListener('toggle', this.#handleDrawerToggle);
    }
  }

  /**
   * Update mobile drawer position based on current header height
   * Includes announcement bars and all other header-group elements
   * Handles hidden elements (display: none, visibility: hidden, height: 0)
   */
  #updateMobileDrawerPosition = () => {
    if (window.innerWidth >= 750) return;

    requestAnimationFrame(() => {
      const totalHeight = this.#readMobileDrawerTop();
      if (totalHeight !== null) {
        this.#applyMobileDrawerTop(totalHeight);
      }
    });
  };

  /**
   * Initialize sticky behavior based on current effective mode
   */
  #initializeStickyBehavior = () => {
    const effectiveStickyMode = this.#getEffectiveStickyMode();
    if (effectiveStickyMode) {
      this.#observeStickyPosition(effectiveStickyMode === 'always');

      if (effectiveStickyMode === 'scroll-up' || effectiveStickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#scrollRafId) cancelAnimationFrame(this.#scrollRafId);
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    window.removeEventListener('resize', this.#handleResize);
    const drawerContainer = document.querySelector('#Details-menu-drawer-container');
    if (drawerContainer) {
      drawerContainer.removeEventListener('toggle', this.#handleDrawerToggle);
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  // Update header group height on resize of any child
  if (headerGroup) {
    let headerGroupRafId = null;
    let lastHeaderGroupHeight = null;
    const resizeObserver = new ResizeObserver(() => {
      if (headerGroupRafId) return;
      headerGroupRafId = requestAnimationFrame(() => {
        headerGroupRafId = null;
        const headerGroupHeight = calculateHeaderGroupHeight(header, headerGroup);
        if (lastHeaderGroupHeight !== headerGroupHeight) {
          document.body.style.setProperty('--header-group-height', `${headerGroupHeight}px`);
          lastHeaderGroupHeight = headerGroupHeight;
        }
      });
    });

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement)) continue;
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
